/** Pure date maths of the calendar popover: Monday-first month grids and keyboard moves. */
import { addDays, addMonths, isIsoDate, periodEnd, periodOf, periodStart } from '@outsource/shared/dates';

const DAYS_PER_WEEK = 7;
/** Six weeks always fit a month and keep the popover the same height. */
const GRID_DAYS = 6 * DAYS_PER_WEEK;

export const WEEKDAYS_RU = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'] as const;

/** 0 for Monday … 6 for Sunday. */
export function weekdayIndex(date: string): number {
  return (new Date(`${date}T00:00:00Z`).getUTCDay() + DAYS_PER_WEEK - 1) % DAYS_PER_WEEK;
}

/** The 42 days shown for a month, from the Monday on or before its 1st. */
export function monthGrid(period: string): string[] {
  const first = periodStart(period);
  const start = addDays(first, -weekdayIndex(first));
  return Array.from({ length: GRID_DAYS }, (_, index) => addDays(start, index));
}

/** The same day `months` later, clamped to the last day of a shorter month (31 Jan → 28 Feb). */
export function shiftMonths(date: string, months: number): string {
  const period = addMonths(periodOf(date), months);
  const sameDay = `${period}-${date.slice(8, 10)}`;
  const last = periodEnd(period);
  return sameDay > last ? last : sameDay;
}

const DAY_STEPS: Readonly<Record<string, number>> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
const MONTH_STEPS: Readonly<Record<string, number>> = { PageUp: -1, PageDown: 1 };

/** The day a grid key moves focus to, or null for a key the grid does not handle. */
export function movedDay(date: string, key: string): string | null {
  const days = DAY_STEPS[key];
  if (days !== undefined) return addDays(date, days);
  const months = MONTH_STEPS[key];
  if (months !== undefined) return shiftMonths(date, months);
  if (key === 'Home') return addDays(date, -weekdayIndex(date));
  if (key === 'End') return addDays(date, DAYS_PER_WEEK - 1 - weekdayIndex(date));
  return null;
}

/** A stored input value the calendar can show, or null for '' / a partly typed date. */
export const validDate = (value: string): string | null => (isIsoDate(value) ? value : null);
